import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Input, Label, Spinner } from '@lensora/ui'
import { useQuery, useMutation } from '@tanstack/react-query'
import * as React from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'


import { api, ApiError } from '@/lib/api'
import { authClient } from '@/lib/auth'

type InviteInfo = {
  email: string
  role: 'admin' | 'member'
  tenantName: string
  tenantSlug: string
  expiresAt: string
}

type AcceptResult = {
  requiresLogin: boolean
  session: { accessToken: string; refreshToken: string; expiresAt: number } | null
}

export function InvitePage() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const token = params.get('token') ?? ''

  const [fullName, setFullName] = React.useState('')
  const [password, setPassword] = React.useState('')
  const [formError, setFormError] = React.useState<string | null>(null)

  const { data: invite, isLoading, error: loadError } = useQuery({
    queryKey: ['invite', token],
    queryFn: () => api.get<InviteInfo>(`/api/invitations/${token}`),
    enabled: !!token,
    retry: false,
  })

  const accept = useMutation({
    mutationFn: () =>
      api.post<AcceptResult>(`/api/invitations/${token}/accept`, { fullName, password }),
    onSuccess: async (result) => {
      if (result.session) {
        await authClient.setSession(result.session.accessToken, result.session.refreshToken)
        navigate('/dashboard', { replace: true })
      } else {
        navigate('/login?invited=1', { replace: true })
      }
    },
    onError: (err) => {
      setFormError(err instanceof ApiError ? err.message : 'Something went wrong')
    },
  })

  if (!token) {
    return <InviteShell><p className="text-feedback-error">Invalid invite link.</p></InviteShell>
  }

  if (isLoading) {
    return <InviteShell><Spinner size="lg" /></InviteShell>
  }

  if (loadError) {
    const msg = loadError instanceof ApiError ? loadError.message : 'This invite link is invalid or has expired.'
    return <InviteShell><p className="text-feedback-error">{msg}</p></InviteShell>
  }

  return (
    <InviteShell>
      <Card>
        <CardHeader>
          <CardTitle>Join {invite!.tenantName}</CardTitle>
          <CardDescription>
            You've been invited as <strong>{invite!.role}</strong>. Set up your account to continue.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form
            onSubmit={(e) => { e.preventDefault(); setFormError(null); accept.mutate() }}
            className="space-y-4"
          >
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={invite!.email} disabled />
            </div>
            <div className="space-y-2">
              <Label htmlFor="fullName">Full name</Label>
              <Input
                id="fullName"
                type="text"
                placeholder="Jane Smith"
                required
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="Min. 8 characters"
                minLength={8}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            {formError && <p className="text-sm text-feedback-error">{formError}</p>}
            <Button type="submit" className="w-full" disabled={accept.isPending}>
              {accept.isPending ? 'Creating account…' : 'Accept invitation'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </InviteShell>
  )
}

function InviteShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-raised p-4">
      <div className="w-full max-w-sm space-y-6">
        <div className="text-center">
          <div className="mx-auto mb-4 h-10 w-10 rounded-lg bg-brand-primary" />
          <h1 className="text-2xl font-bold text-text">You're invited</h1>
        </div>
        {children}
      </div>
    </div>
  )
}
